import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Box, Typography, Paper, Button, Modal, TextField, Chip, Divider, Badge } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';

// React Icons imports
import {
    FiGrid,
    FiList,
    FiUsers,
    FiHome,
    FiMapPin,
    FiMonitor,
    FiUser,
    FiUserPlus,
    FiClipboard,
    FiAlertTriangle,
    FiXCircle,
    FiFileText,
    FiSettings,
    FiLock,
    FiMessageSquare,
    FiLogOut,
    FiChevronDown
} from 'react-icons/fi';
import {
    FaHospital,
    FaUserMd,
    FaClinicMedical
} from 'react-icons/fa';
import {
    MdDashboard,
    MdPeople,
    MdLocalHospital,
    MdMedicalServices
} from 'react-icons/md';
import { GiHealthNormal } from 'react-icons/gi';

import sideRight from "../../assets/images/sideRight.svg";
import curebayIcon from "../../assets/images/curebayIcon.svg";

import Dashboard from './dashboard';
import CompletedOrders from './completedorders';
import MappingList from './mappingList';
import MobiLabusers from './mobilabUserList';
import ChangePassword from '../login/changePassword';
import { APP_ROUTES } from '../../routes/approutes';
import apiService from '../../services/actions';
import { formatToCurrentDateTime } from '../common';
import CCBulkUpload from './ccBulkUpload';
import MobilabClinicList from './mobilabecliniclist';
import MobilabUserListNew from './mobilabUserListNew';
import LabPartnerErrorLogList from './errorLogList';
import ReportFailureList from './reportFailure';
import LabOnboarding from './LabOnboarding';
import CCOnboardingWorkflow from './ccOnboardingWorkflow';
import Configuration from './Configuration';
import CorporateList from './CorporateList';
import CorporateApproval from './CorporateApproval';


const drawerWidth = 280;

// Maps a sidebar menu item to the "screen" ID and "screenName" returned by the
// /diagnostic/api/lab/login-workflow/config API. A menu item is only rendered
// if it is present with status === 1 in that user's config response.
const SCREEN_NAMES = {
    DASHBOARD: ['1', 'ORDER_DASHBOARD', 'ORDER DASHBOARD', 'ORDER_SCREEN', 'DASHBOARD'],
    ORDERS: ['2', 'ORDER', 'ORDERS', 'ORDER_SCREEN', 'COMPLETEDORDERS', 'COMPLETED ORDERS'],
    MOBILAB: ['3', 'MOBILAB_ONBOARDING', 'MOBILAB ONBOARDING', 'MOBILAB', 'ECLINIC'],
    LAB_SUPPORT: ['4', 'LAB_SUPPORT', 'LAB SUPPORT', 'SUPPORT', 'SUPPORT_SCREEN', 'SUPPORTSCREEN'],
    BOOKING_FAILURE: ['4', 'LAB_SUPPORT', 'LAB SUPPORT', 'SUPPORT', 'BOOKING_FAILURE', 'BOOKING FAILURE'],
    REPORT_FAILURE: ['4', 'LAB_SUPPORT', 'LAB SUPPORT', 'SUPPORT', 'REPORT_FAILURE', 'REPORT FAILURE'],
    LAB_ONBOARDING: ['5', 'LAB_ONBOARDING', 'LAB ONBOARDING', 'LAB_ONBOARD', 'LAB ONBOARD'],
    CC_ONBOARDING: ['6', 'CC_ONBOARDING', 'CC ONBOARDING', 'CC_ONBOARD', 'CC ONBOARD'],
    BUSINESS_APPROVAL: ['7', 'BUSINESS_APPROVAL', 'BUSINESS APPROVAL'],
    FINANCE_APPROVAL: ['8', 'FINANCE_APPROVAL', 'FINANCE APPROVAL'],
    CONFIGURATION: ['9', 'CONFIGURATION', 'CONFIG', 'CONFIGURATION_SCREEN'],
    CORPORATE: ['10', 'CORPORATE', 'CORPORATE_SCREEN', 'CORPORATES', 'CORPORATE LIST', 'CORPORATE_LIST', 'CORPORATELIST', 'CORPORATE_ONBOARDING', 'CORPORATE ONBOARDING'],
    CC_BULK_UPLOAD: ['CC_BULK_UPLOAD', 'CC BULK UPLOAD', 'BULK_UPLOAD', 'BULK'],
};

// Route for each top-level menu item that navigates directly (keyed by
// item `name`) — shared by handleMenuClick and the first-available-screen
// redirect below so the two never drift apart.
const TOP_LEVEL_ROUTE_MAP = {
    'Dashboard': APP_ROUTES.DASHBOARD,
    'Received Orders': APP_ROUTES.RECEIVEDORDERS,
    'All Orders': APP_ROUTES.ALLORDERS,
    'Orders': APP_ROUTES.COMPLETEDORDERS,
    'Reports': APP_ROUTES.REPORTS,
    'Lab Details': APP_ROUTES.LABLIST,
    'SM Collections': APP_ROUTES.COLLECTION,
    'Home Collection Requests': APP_ROUTES.HOMECC,
    'CC Bulk Upload': APP_ROUTES.CCBULK,
    'Mobilab': APP_ROUTES.MOBILABECLIST,
    'Configuration': APP_ROUTES.CONFIGURATION,
    'Corporate': APP_ROUTES.CORPORATELIST,
};

// Route for each submenu item that navigates directly (keyed by item
// `key`) — shared by submenuClick and the first-available-screen redirect.
const CHILD_ROUTE_MAP = {
    'Eclinic': APP_ROUTES.MOBILABECLIST,
    'User': APP_ROUTES.MOBIUSERNEW,
    'BookingFailure': APP_ROUTES.LOG,
    'ReportFailure': APP_ROUTES.REPORTFAIL,
    'LabOnboarding': APP_ROUTES.LABONBOARD,
    'CCOnboarding': APP_ROUTES.ONBARDING,
    'CC Onboarding': APP_ROUTES.ONBARDING, // Handle both cases
    'Corporate': APP_ROUTES.CORPORATELIST,
    'CorporateOnboarding': APP_ROUTES.CORPORATELIST,
    'Corporate Onboarding': APP_ROUTES.CORPORATELIST,
    'CorporateList': APP_ROUTES.CORPORATELIST,
    'BusinessApproval': APP_ROUTES.BUSINESSAPPROVAL,
    'FinanceApproval': APP_ROUTES.FINANCEAPPROVAL,
};

const isScreenAllowed = (screenSpec, allowedScreens) => {
    if (!screenSpec) return false;
    if (!allowedScreens || !Array.isArray(allowedScreens) || allowedScreens.length === 0) return false;
    if (allowedScreens.includes('*')) return true;

    const targets = Array.isArray(screenSpec) ? screenSpec : [screenSpec];
    return targets.some((t) => {
        const str = String(t).trim().toUpperCase();
        const compact = str.replace(/[\s_-]+/g, '');
        return allowedScreens.includes(str) || allowedScreens.includes(compact);
    });
};

// Prunes a menu tree down to only the items whose `screen` is present in
// `allowedScreens`. Items without a `screen` of their own (e.g. a parent
// like "Onboarding" that only groups children) are kept as long as at
// least one of their children remains visible.
const filterMenuByScreens = (items, allowedScreens) => {
    return items.reduce((visible, item) => {
        if (item.children && item.children.length > 0) {
            const children = filterMenuByScreens(item.children, allowedScreens);
            const ownScreenOk = item.screen ? isScreenAllowed(item.screen, allowedScreens) : true;
            if (children.length > 0 && ownScreenOk) {
                visible.push({ ...item, children });
            }
            return visible;
        }
        if (item.screen && isScreenAllowed(item.screen, allowedScreens)) {
            visible.push(item);
        }
        return visible;
    }, []);
};

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: '#ffffff',
    color: '#1E293B',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.05)',
    borderBottom: '1px solid #E2E8F0',
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
            '& .MuiListItemIcon-root': {
                minWidth: 0,
                marginRight: 0,
                justifyContent: 'center',
                width: '100%',
            },
            '& .MuiListItemText-root': {
                display: 'none',
            },
            '& .MuiListItemButton-root': {
                justifyContent: 'center',
                px: 2.5,
            },
        }),
    }),
);

const MenuListItem = styled(ListItem)(({ theme, selected }) => ({
    backgroundColor: selected ? '#E1F4FF' : 'transparent',
    cursor: 'pointer',
    color: selected ? '#1C6CE5' : 'inherit',
    transition: theme.transitions.create(['background-color', 'color'], {
        duration: theme.transitions.duration.short,
    }),
    '&:hover': {
        backgroundColor: '#E1F4FF',
    },
    padding: 0,
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme, open }) => ({
    minWidth: open ? 56 : 'auto',
    justifyContent: 'center',
    display: 'flex',
    '& svg': {
        width: open ? '24px' : '20px',
        height: '20px',
        transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.short,
        }),
    },
}));

const SubMenuPopover = styled(Popover)(({ theme }) => ({
    '& .MuiPaper-root': {
        marginLeft: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        borderRadius: '8px',
    },
}));

// Icon wrapper component for consistent styling
const IconWrapper = ({ icon: Icon, size = 24, color = '#333' }) => {
    return <Icon size={size} color={color} />;
};

const HomePage = ({ children }) => {
    const loginUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('loginObj') || 'null');
        } catch {
            return null;
        }
    }, []);

    const userId = useMemo(() => {
        return (
            loginUser?.userSession?.userCode ||
            loginUser?.user?.code ||
            loginUser?.userCode ||
            localStorage.getItem('userCode') ||
            localStorage.getItem('access_name') ||
            ''
        );
    }, [loginUser]);

    const currentDateTime = formatToCurrentDateTime(new Date());
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const [changePasswordModal, setChangePasswordModal] = useState(false);
    const [feedbackModal, setFeedbackModal] = useState(false);
    const location = useLocation();
    const path = location.pathname;
    const activeMenu = path.split('/').pop() || 'Dashboard';
    const [anchorEl, setAnchorEl] = useState(null);
    const [feedback, setFeedback] = useState({
        subject: '',
        feedback: '',
    });
    const [openSubMenus, setOpenSubMenus] = useState({});
    const [anchorElSub, setAnchorElSub] = useState(null);
    const [hoveredMenu, setHoveredMenu] = useState(null);
    const [subMenuTimeout, setSubMenuTimeout] = useState(null);
    const popoverRef = useRef(null);
    const [userPhotoUrl, setUserPhotoUrl] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const loadUserPhoto = async () => {
            const rawPhoto = loginUser?.user?.photoName || loginUser?.photoName || loginUser?.userSession?.photoName;
            if (!rawPhoto || typeof rawPhoto !== 'string') {
                setUserPhotoUrl(null);
                return;
            }
            if (rawPhoto.startsWith('http://') || rawPhoto.startsWith('https://') || rawPhoto.startsWith('blob:') || rawPhoto.startsWith('data:')) {
                setUserPhotoUrl(rawPhoto);
                return;
            }
            try {
                const signedUrl = await apiService.getDocumentUrl(rawPhoto);
                if (isMounted && signedUrl) {
                    setUserPhotoUrl(signedUrl);
                } else if (isMounted) {
                    setUserPhotoUrl(null);
                }
            } catch (err) {
                console.error('Failed to load user photo:', err);
                if (isMounted) setUserPhotoUrl(null);
            }
        };

        loadUserPhoto();
        return () => {
            isMounted = false;
        };
    }, [loginUser]);

    const displayName = useMemo(() => {
        const u = loginUser?.user;
        const nameParts = [u?.firstName, u?.lastName].filter(Boolean);
        if (nameParts.length > 0) return nameParts.join(' ');
        return u?.userName || u?.name || localStorage.getItem('access_name') || 'User';
    }, [loginUser]);

    const userInitials = useMemo(() => {
        const u = loginUser?.user;
        const first = u?.firstName?.trim() || '';
        const last = u?.lastName?.trim() || '';
        if (first && last) {
            return `${first[0]}${last[0]}`.toUpperCase();
        }
        if (first) {
            return first.slice(0, 2).toUpperCase();
        }
        const alt = u?.userName || u?.name || localStorage.getItem('access_name') || 'User';
        return alt.trim().slice(0, 2).toUpperCase();
    }, [loginUser]);

    const userRoleDisplay = useMemo(() => {
        const role =
            loginUser?.user?.userRolesList?.[0]?.roleName ||
            loginUser?.user?.userRolesList?.[0]?.roleCode ||
            localStorage.getItem('userRole') ||
            'Diagnostic Admin';
        return role.replace(/_/g, ' ').trim();
    }, [loginUser]);

    const userEmail = loginUser?.user?.email || '';
    const userPhone = loginUser?.user?.phoneNumber || '';


    const isDiagExecutive = useMemo(() => {
        return loginUser?.user?.userRolesList?.[0]?.roleCode === "DIAG_EXECUTIVE ";
    }, [loginUser]);

    const isDiagnosticsSupport = useMemo(() => {
        return loginUser?.user?.userRolesList?.[0]?.roleCode === "DIAGNOSTICS_SUPPORT";
    }, [loginUser]);

    // Initialize from cache if available so UI renders immediately without redundant API call
    const [allowedScreens, setAllowedScreens] = useState(() => {
        try {
            const uid =
                loginUser?.userSession?.userCode ||
                loginUser?.user?.code ||
                loginUser?.userCode ||
                localStorage.getItem('userCode') ||
                localStorage.getItem('access_name') ||
                '';
            const cached = sessionStorage.getItem(`allowedScreens_${uid}`) || sessionStorage.getItem('allowedScreens');
            if (cached) {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed)) {
                    return parsed;
                }
            }
        } catch {
            // ignore
        }
        return null;
    });

    useEffect(() => {
        if (!userId) {
            setAllowedScreens([]);
            return undefined;
        }

        const cacheKey = `allowedScreens_${userId}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed)) {
                    setAllowedScreens(parsed);
                    return undefined;
                }
            } catch {
                // ignore parse error and re-fetch
            }
        }

        let isActive = true;

        (async () => {
            try {
                const res = await apiService.getLoginWorkflowConfig(userId);
                if (!isActive) return;
                const rawList = res?.data?.data || res?.data?.responseData || res?.data?.content || res?.data || res;
                const rows = Array.isArray(rawList) ? rawList : [];

                const activeTokens = new Set();
                rows.forEach((row) => {
                    const status = row.status !== undefined && row.status !== null ? Number(row.status) : 1;
                    if (status === 1) {
                        if (row.screen !== undefined && row.screen !== null) {
                            activeTokens.add(String(row.screen).trim().toUpperCase());
                        }
                        if (row.screenName) {
                            const nameStr = String(row.screenName).trim().toUpperCase();
                            activeTokens.add(nameStr);
                            activeTokens.add(nameStr.replace(/[\s_-]+/g, ''));
                        }
                    }
                });
                const screens = Array.from(activeTokens);

                sessionStorage.setItem(cacheKey, JSON.stringify(screens));
                sessionStorage.setItem('allowedScreens', JSON.stringify(screens));
                setAllowedScreens(screens);
            } catch (err) {
                console.error('Unable to load login-workflow config:', err);
                setAllowedScreens([]);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [userId]);

    const handleInputChange = useCallback((field, value) => {
        setFeedback(prev => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const sentFeedback = async () => {
        const payload = {
            subject: feedback.subject,
            content: feedback.feedback,
            modifiedDate: currentDateTime,
            modifiedBy: loginUser?.user?.code,
            createdDate: currentDateTime,
            createdBy: loginUser?.user?.code,
            fromCode: loginUser?.user?.code,
            status: 1
        };

        try {
            const res = await apiService.sentFeedback(payload);
            if (res?.status === 200 && res?.data) {
                setFeedbackModal(false);
            }
        } catch (error) {
            console.error('API request error:', error);
        }
    };

    const handleClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleDrawerClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleMenuClick = useCallback((menu) => {
        if (TOP_LEVEL_ROUTE_MAP[menu]) {
            navigate(TOP_LEVEL_ROUTE_MAP[menu]);
            setOpen(false);
        }
    }, [navigate]);

    const openChangePassword = useCallback((type) => {
        if (type === 'CP') {
            setChangePasswordModal(true);
        } else if (type === 'FB') {
            setFeedbackModal(true);
        }
    }, []);

    const handleCloseAdvancedFilterModal = useCallback(() => {
        setChangePasswordModal(false);
        setFeedbackModal(false);
    }, []);

    const handleSubHoverOpen = useCallback((event, menu) => {
        if (!open && menu?.children) {
            if (subMenuTimeout) {
                clearTimeout(subMenuTimeout);
                setSubMenuTimeout(null);
            }
            setAnchorElSub(event.currentTarget);
            setHoveredMenu(menu);
        }
    }, [open, subMenuTimeout]);

    const handleSubHoverClose = useCallback(() => {
        const timeout = setTimeout(() => {
            setAnchorElSub(null);
            setHoveredMenu(null);
        }, 150);
        setSubMenuTimeout(timeout);
    }, []);

    const handlePopoverEnter = useCallback(() => {
        if (subMenuTimeout) {
            clearTimeout(subMenuTimeout);
            setSubMenuTimeout(null);
        }
    }, [subMenuTimeout]);

    const handlePopoverLeave = useCallback(() => {
        handleSubHoverClose();
    }, [handleSubHoverClose]);

    const handleParentMouseLeave = useCallback((e) => {
        const relatedTarget = e.relatedTarget;
        if (popoverRef.current?.contains(relatedTarget)) {
            return;
        }
        handleSubHoverClose();
    }, [handleSubHoverClose]);

    // Handles clicks on sub-menu items (both the inline expanded-drawer
    // submenu and the collapsed-drawer hover popover route through here).
    const submenuClick = useCallback((to) => {
        if (CHILD_ROUTE_MAP[to]) {
            navigate(CHILD_ROUTE_MAP[to]);
        } else {
            navigate(APP_ROUTES.MAPLIST, { state: { toValue: to } });
        }
        setOpen(false);
        handleSubHoverClose();
    }, [navigate, handleSubHoverClose]);

    // Automatically close sidebar when navigation/route changes
    useEffect(() => {
        setOpen(false);
    }, [location.pathname, location.state]);

    const handleParentClick = useCallback((menu) => {
        if (menu?.children) {
            if (open) {
                setOpenSubMenus(prev => ({
                    ...prev,
                    [menu.name]: !prev[menu.name],
                }));
            }
        } else {
            handleMenuClick(menu.name);
        }
    }, [open, handleMenuClick]);

    const renderMenuListItem = useCallback((menu, index) => {
        const hasChildren = menu.children && menu.children.length > 0;
        const isChildActive = hasChildren && menu.children.some((child) => {
            const route = CHILD_ROUTE_MAP[child.key];
            return (
                route &&
                (path.toLowerCase() === route.toLowerCase() ||
                 activeMenu.toLowerCase() === route.replace('/', '').toLowerCase())
            );
        });
        const isSelected = activeMenu.toLowerCase() === menu.url?.toLowerCase() || isChildActive;
        const isSubMenuOpen = Boolean(openSubMenus[menu.name]);
        const menuKey = menu.name || index;

        return (
            <Box key={menuKey}>
                <MenuListItem
                    selected={isSelected}
                    onClick={() => handleParentClick(menu)}
                    onMouseEnter={(e) => handleSubHoverOpen(e, menu)}
                    onMouseLeave={hasChildren ? handleParentMouseLeave : undefined}
                    sx={{
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            justifyContent: open ? 'flex-start' : 'center',
                            minHeight: 48,
                            px: open ? 3 : 2,
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <StyledListItemIcon open={open}>
                            {menu.iconComponent}
                        </StyledListItemIcon>
                        {open && <ListItemText primary={menu.name} />}
                        {hasChildren && !open && (
                            <ChevronRight sx={{ fontSize: 16 }} />
                        )}
                        {hasChildren && open && (
                            <ChevronRight
                                sx={{
                                    fontSize: 16,
                                    ml: 'auto',
                                    transform: isSubMenuOpen ? 'rotate(90deg)' : 'none',
                                    transition: 'transform 0.2s',
                                }}
                            />
                        )}
                    </Box>
                </MenuListItem>

                {open && isSubMenuOpen && hasChildren && (
                    <Box sx={{ pl: 4 }}>
                        {menu.children.map((sub, i) => {
                            const subKey = sub.key || sub.name || i;
                            const isDisabled = Boolean(sub.disabled);
                            const isChildSelected = Boolean(
                                CHILD_ROUTE_MAP[sub.key] && (
                                    path.toLowerCase() === CHILD_ROUTE_MAP[sub.key].toLowerCase() ||
                                    activeMenu.toLowerCase() === CHILD_ROUTE_MAP[sub.key].replace('/', '').toLowerCase()
                                )
                            );
                            return (
                                <MenuListItem
                                    key={subKey}
                                    selected={isChildSelected}
                                    onClick={!isDisabled ? () => submenuClick(sub.key) : undefined}
                                    sx={{
                                        pl: 3,
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        opacity: isDisabled ? 0.45 : 1,
                                        '&:hover': {
                                            backgroundColor: isDisabled ? 'transparent' : '#E1F4FF',
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            pl: 3,
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        <StyledListItemIcon open={open}>
                                            {sub.iconComponent}
                                        </StyledListItemIcon>
                                        <ListItemText primary={sub.name} />
                                    </Box>
                                </MenuListItem>
                            );
                        })}
                    </Box>
                )}
            </Box>
        );
    }, [activeMenu, open, openSubMenus, path, handleParentClick, handleSubHoverOpen, handleParentMouseLeave, submenuClick]);

    const getMenuList = useMemo(() => {
        if (isDiagExecutive) {
            return [
                {
                    name: 'Dashboard',
                    iconComponent: <MdDashboard size={24} color="#1C6CE5" />,
                    url: 'dashboard',
                    screen: SCREEN_NAMES.DASHBOARD,
                },
                {
                    name: 'CC Bulk Upload',
                    iconComponent: <FiClipboard size={24} color="#1C6CE5" />,
                    url: 'ccBulk',
                    screen: SCREEN_NAMES.CC_BULK_UPLOAD,
                },
            ];
        }

        if (isDiagnosticsSupport) {
            return [
                {
                    name: 'Lab Support',
                    iconComponent: <FiAlertTriangle size={24} color="#1C6CE5" />,
                    screen: SCREEN_NAMES.LAB_SUPPORT,
                    children: [
                        {
                            name: 'Booking Failure',
                            key: 'BookingFailure',
                            iconComponent: <FiXCircle size={20} color="#1C6CE5" />,
                            screen: SCREEN_NAMES.BOOKING_FAILURE,
                        },
                        {
                            name: 'Report Failure',
                            key: 'ReportFailure',
                            iconComponent: <FiFileText size={20} color="#1C6CE5" />,
                            screen: SCREEN_NAMES.REPORT_FAILURE,
                        },
                    ],
                },
            ];
        }

        return [
            {
                name: 'Dashboard',
                iconComponent: <MdDashboard size={24} color="#1C6CE5" />,
                url: 'dashboard',
                screen: SCREEN_NAMES.DASHBOARD,
            },
            {
                name: 'Orders',
                iconComponent: <FiList size={24} color="#1C6CE5" />,
                url: 'completedOrders',
                screen: SCREEN_NAMES.ORDERS,
            },
            {
                name: 'Mobilab',
                iconComponent: <GiHealthNormal size={24} color="#1C6CE5" />,
                url: 'mobilabecliniclist',
                screen: SCREEN_NAMES.MOBILAB,
            },
            {
                name: 'Onboarding',
                iconComponent: <FiUserPlus size={24} color="#1C6CE5" />,
                children: [
                    {
                        name: 'Lab Onboarding',
                        key: 'LabOnboarding',
                        iconComponent: <FaClinicMedical size={20} color="#1C6CE5" />,
                        screen: SCREEN_NAMES.LAB_ONBOARDING,
                    },
                    {
                        name: 'CC Onboarding',
                        key: 'CCOnboarding',  // Notice this is 'CCOnboarding'
                        iconComponent: <FiUserPlus size={20} color="#1C6CE5" />,
                        screen: SCREEN_NAMES.CC_ONBOARDING,
                    },
                    {
                        name: 'Corporate',
                        key: 'Corporate',
                        iconComponent: <FaHospital size={20} color="#1C6CE5" />,
                        screen: SCREEN_NAMES.CORPORATE,
                    },
                    {
                        name: 'Business Approval',
                        key: 'BusinessApproval',
                        iconComponent: <FiClipboard size={20} color="#1C6CE5" />,
                        screen: SCREEN_NAMES.BUSINESS_APPROVAL,
                    },
                    {
                        name: 'Finance Approval',
                        key: 'FinanceApproval',
                        iconComponent: <FiFileText size={20} color="#1C6CE5" />,
                        screen: SCREEN_NAMES.FINANCE_APPROVAL,
                    },
                ],
            },
            {
                name: 'Lab Support',
                iconComponent: <FiAlertTriangle size={24} color="#1C6CE5" />,
                screen: SCREEN_NAMES.LAB_SUPPORT,
                children: [
                    {
                        name: 'Booking Failure',
                        key: 'BookingFailure',
                        iconComponent: <FiXCircle size={20} color="#1C6CE5" />,
                        screen: SCREEN_NAMES.BOOKING_FAILURE,
                    },
                    {
                        name: 'Report Failure',
                        key: 'ReportFailure',
                        iconComponent: <FiFileText size={20} color="#1C6CE5" />,
                        screen: SCREEN_NAMES.REPORT_FAILURE,
                    },
                ],
            },
            {
                name: 'Configuration',
                iconComponent: <FiSettings size={24} color="#1C6CE5" />,
                url: 'configuration',
                screen: SCREEN_NAMES.CONFIGURATION,
            },
        ];
    }, [isDiagExecutive, isDiagnosticsSupport]);

    // Automatically expand the parent submenu if the active route is a child item
    useEffect(() => {
        getMenuList.forEach((m) => {
            if (m.children && m.children.length > 0) {
                const hasActiveChild = m.children.some((child) => {
                    const route = CHILD_ROUTE_MAP[child.key];
                    return (
                        route &&
                        (path.toLowerCase() === route.toLowerCase() ||
                         activeMenu.toLowerCase() === route.replace('/', '').toLowerCase())
                    );
                });
                if (hasActiveChild) {
                    setOpenSubMenus((prev) => ({
                        ...prev,
                        [m.name]: true,
                    }));
                }
            }
        });
    }, [path, activeMenu, getMenuList]);

    // Only render menu items whose screen was returned (status === 1) by the
    // login-workflow config API. While that call is still in flight
    // (allowedScreens === null) the nav renders empty rather than flashing
    // items the user may not have access to.
    const MenuList = useMemo(() => {
        if (allowedScreens === null) return [];
        return filterMenuByScreens(getMenuList, allowedScreens);
    }, [getMenuList, allowedScreens]);

    // The route for the first navigable item left in MenuList, in display
    // order — i.e. the first screen this user actually has access to.
    // Parent items are only navigable via their children (Onboarding /
    // Lab Support have no `url` of their own), so we recurse into
    // `children` and only collect leaves that resolve to a real route.
    const firstAvailableRoute = useMemo(() => {
        const routeForLeaf = (item) => TOP_LEVEL_ROUTE_MAP[item.name] || CHILD_ROUTE_MAP[item.key] || null;
        const flatten = (items) =>
            items.reduce((routes, item) => {
                if (item.children && item.children.length > 0) {
                    routes.push(...flatten(item.children));
                } else {
                    const route = routeForLeaf(item);
                    if (route) routes.push(route);
                }
                return routes;
            }, []);
        return flatten(MenuList)[0] || null;
    }, [MenuList]);

    // If a user lands on /dashboard (the app's default post-login route)
    // but Dashboard isn't one of their allowed screens, send them to their
    // first available menu item instead of showing them a screen they
    // don't have access to.
    useEffect(() => {
        if (allowedScreens === null) return; // still loading the config
        const isOnDashboard = path === APP_ROUTES.DASHBOARD;
        const hasDashboardAccess = isScreenAllowed(SCREEN_NAMES.DASHBOARD, allowedScreens);
        if (isOnDashboard && !hasDashboardAccess && firstAvailableRoute) {
            navigate(firstAvailableRoute, { replace: true });
        }
    }, [allowedScreens, path, firstAvailableRoute, navigate]);

    const handleLogout = useCallback(() => {
        localStorage.removeItem('loginObj');
        sessionStorage.clear();
        navigate('/');
    }, [navigate]);

    const renderContent = useMemo(() => {
        if (children) return children;
        const contentMap = {
            'dashboard': <Dashboard />,
            'completedorders': <CompletedOrders />,
            'chemapping': <MappingList />,
            'mapuserlist': <MobiLabusers />,
            'ccbulk': <CCBulkUpload />,
            'mobilabecliniclist': <MobilabClinicList />,
            'mobiusernew': <MobilabUserListNew />,
            'errolog': <LabPartnerErrorLogList />,
            'reportfail': <ReportFailureList />,
            'onboarding': <CCOnboardingWorkflow key="creator_cc" mode="creator" defaultOnboardingType="CC" />,
            'businessapproval': <CCOnboardingWorkflow key="businessApproval" mode="businessApproval" />,
            'financeapproval': <CCOnboardingWorkflow key="financeApproval" mode="financeApproval" />,
            'labonboarding': <CCOnboardingWorkflow key="creator_lab" mode="creator" defaultOnboardingType="LAB" />,
            'configuration': <Configuration />,
            'corporatelist': <CorporateList />,
            'corporateapproval': <CorporateApproval />,
        };

        const key = activeMenu.toLowerCase().replace(/\s+/g, "");
        return contentMap[key] || children || null;
    }, [activeMenu, children]);

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar position="fixed" open={open} elevation={0}>
                <Toolbar
                    sx={{
                        justifyContent: 'space-between',
                        backgroundColor: '#ffffff',
                        minHeight: '64px !important',
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={() => setOpen(!open)}
                            edge="start"
                            sx={{
                                p: 1,
                                borderRadius: '10px',
                                color: '#475569',
                                '&:hover': { bgcolor: '#F1F5F9' },
                                ...(open && { display: 'none' }),
                                ...(!open && { display: 'flex' }),
                            }}
                        >
                            <img src={sideRight} alt="Toggle drawer" style={{ width: 20, height: 20 }} />
                        </IconButton>
                        {!open && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <img src={curebayIcon} alt="CureBay" style={{ height: '36px' }} />
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Interactive User Profile Card */}
                        <Box
                            onClick={handleClick}
                            role="button"
                            tabIndex={0}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                py: 0.6,
                                px: 1.2,
                                borderRadius: '100px',
                                cursor: 'pointer',
                                border: '1px solid',
                                borderColor: Boolean(anchorEl) ? '#93C5FD' : '#E2E8F0',
                                bgcolor: Boolean(anchorEl) ? '#EFF6FF' : '#F8FAFC',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    bgcolor: '#F1F5F9',
                                    borderColor: '#CBD5E1',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                                },
                            }}
                        >
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                variant="dot"
                                sx={{
                                    '& .MuiBadge-badge': {
                                        bgcolor: '#10B981',
                                        color: '#10B981',
                                        boxShadow: '0 0 0 2px #fff',
                                        width: 9,
                                        height: 9,
                                        borderRadius: '50%',
                                    },
                                }}
                            >
                                <Avatar
                                    src={userPhotoUrl || undefined}
                                    alt={displayName}
                                    imgProps={{
                                        onError: () => setUserPhotoUrl(null),
                                    }}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        fontWeight: 700,
                                        fontSize: '0.85rem',
                                        background: userPhotoUrl
                                            ? '#F1F5F9'
                                            : 'linear-gradient(135deg, #004c97 0%, #1c6ce5 100%)',
                                        color: '#ffffff',
                                        boxShadow: '0 2px 4px rgba(0,76,151,0.2)',
                                        border: '2px solid #ffffff',
                                    }}
                                >
                                    {!userPhotoUrl && userInitials}
                                </Avatar>
                            </Badge>

                            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: '#1E293B',
                                        lineHeight: 1.2,
                                        maxWidth: 160,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {displayName}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.72rem',
                                        color: '#64748B',
                                        fontWeight: 500,
                                        lineHeight: 1.1,
                                        maxWidth: 160,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {userRoleDisplay}
                                </Typography>
                            </Box>

                            <FiChevronDown
                                size={16}
                                color="#64748B"
                                style={{
                                    transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}
                            />
                        </Box>

                        {/* Enterprise Profile Popover */}
                        <Popover
                            open={Boolean(anchorEl)}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            PaperProps={{
                                sx: {
                                    mt: 1.5,
                                    width: 290,
                                    borderRadius: '16px',
                                    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12), 0 4px 12px rgba(15, 23, 42, 0.08)',
                                    border: '1px solid #E2E8F0',
                                    overflow: 'hidden',
                                },
                            }}
                        >
                            {/* User Header Card inside Popover */}
                            <Box
                                sx={{
                                    p: 2.5,
                                    background: 'linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)',
                                    borderBottom: '1px solid #E2E8F0',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                }}
                            >
                                <Avatar
                                    src={userPhotoUrl || undefined}
                                    alt={displayName}
                                    imgProps={{
                                        onError: () => setUserPhotoUrl(null),
                                    }}
                                    sx={{
                                        width: 58,
                                        height: 58,
                                        fontWeight: 700,
                                        fontSize: '1.25rem',
                                        background: userPhotoUrl
                                            ? '#F1F5F9'
                                            : 'linear-gradient(135deg, #004c97 0%, #1c6ce5 100%)',
                                        color: '#ffffff',
                                        boxShadow: '0 4px 12px rgba(0, 76, 151, 0.25)',
                                        border: '3px solid #ffffff',
                                        mb: 1,
                                    }}
                                >
                                    {!userPhotoUrl && userInitials}
                                </Avatar>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0F172A', lineHeight: 1.3 }}>
                                    {displayName}
                                </Typography>
                                <Chip
                                    label={userRoleDisplay}
                                    size="small"
                                    sx={{
                                        mt: 0.75,
                                        height: 22,
                                        fontSize: '0.72rem',
                                        fontWeight: 600,
                                        bgcolor: '#DBEAFE',
                                        color: '#1D4ED8',
                                        borderRadius: '6px',
                                    }}
                                />
                                {userEmail && (
                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 1, wordBreak: 'break-all' }}>
                                        {userEmail}
                                    </Typography>
                                )}
                                {userPhone && (
                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.2 }}>
                                        {userPhone}
                                    </Typography>
                                )}
                            </Box>

                            {/* Menu Options */}
                            <Box sx={{ p: 1 }}>
                                <MenuItem
                                    onClick={() => {
                                        handleClose();
                                        openChangePassword('CP');
                                    }}
                                    sx={{
                                        py: 1.2,
                                        px: 1.5,
                                        borderRadius: '8px',
                                        gap: 1.5,
                                        transition: 'all 0.15s ease',
                                        '&:hover': { bgcolor: '#F1F5F9' },
                                    }}
                                >
                                    <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FiLock size={16} color="#475569" />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                                            Change Password
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                                            Update your credentials
                                        </Typography>
                                    </Box>
                                </MenuItem>

                                {/* <MenuItem
                                    onClick={() => {
                                        handleClose();
                                        openChangePassword('FB');
                                    }}
                                    sx={{
                                        py: 1.2,
                                        px: 1.5,
                                        borderRadius: '8px',
                                        gap: 1.5,
                                        transition: 'all 0.15s ease',
                                        '&:hover': { bgcolor: '#F1F5F9' },
                                    }}
                                >
                                    <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FiMessageSquare size={16} color="#475569" />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                                            Feedback
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                                            Help us improve the portal
                                        </Typography>
                                    </Box>
                                </MenuItem> */}

                                <Divider sx={{ my: 0.75 }} />

                                <MenuItem
                                    onClick={() => {
                                        handleClose();
                                        handleLogout();
                                    }}
                                    sx={{
                                        py: 1.2,
                                        px: 1.5,
                                        borderRadius: '8px',
                                        gap: 1.5,
                                        color: '#DC2626',
                                        transition: 'all 0.15s ease',
                                        '&:hover': { bgcolor: '#FEF2F2' },
                                    }}
                                >
                                    <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FiLogOut size={16} color="#DC2626" />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#DC2626' }}>
                                        Log out
                                    </Typography>
                                </MenuItem>
                            </Box>
                        </Popover>
                    </Box>
                </Toolbar>
            </AppBar>
            <Drawer variant="permanent" open={open}>
                <DrawerHeader>
                    {open && (
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <img src={curebayIcon} alt="CureBay" style={{ height: '46px' }} />
                        </Box>
                    )}
                    <IconButton onClick={handleDrawerClose}>
                        <img src={sideRight} alt="Close drawer" />
                    </IconButton>
                </DrawerHeader>
                <List>
                    {MenuList.map((menu, index) => renderMenuListItem(menu, index))}
                </List>
            </Drawer>

            <SubMenuPopover
                open={Boolean(anchorElSub) && !open && Boolean(hoveredMenu?.children?.length)}
                anchorEl={anchorElSub}
                onClose={handleSubHoverClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        marginLeft: '8px',
                    }
                }}
                disableRestoreFocus
                ref={popoverRef}
            >
                <Paper
                    sx={{ padding: 1, minWidth: 200 }}
                    onMouseEnter={handlePopoverEnter}
                    onMouseLeave={handlePopoverLeave}
                >
                    {hoveredMenu?.children?.map((sub, i) => {
                        const subKey = sub.key || sub.name || i;
                        const isDisabled = Boolean(sub.disabled);
                        const isChildSelected = Boolean(
                            CHILD_ROUTE_MAP[sub.key] && (
                                path.toLowerCase() === CHILD_ROUTE_MAP[sub.key].toLowerCase() ||
                                activeMenu.toLowerCase() === CHILD_ROUTE_MAP[sub.key].replace('/', '').toLowerCase()
                            )
                        );
                        return (
                            <MenuItem
                                key={subKey}
                                selected={isChildSelected}
                                disabled={isDisabled}
                                onClick={!isDisabled ? () => {
                                    submenuClick(sub.key);
                                    handleSubHoverClose();
                                } : undefined}
                                sx={{
                                    padding: '8px 16px',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    opacity: isDisabled ? 0.45 : 1,
                                    backgroundColor: isChildSelected ? '#E1F4FF' : 'transparent',
                                    color: isChildSelected ? '#1C6CE5' : 'inherit',
                                    '&:hover': {
                                        backgroundColor: isDisabled ? 'transparent' : '#E1F4FF',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {sub.iconComponent}
                                    </Box>
                                    <Typography variant="body2">{sub.name}</Typography>
                                </Box>
                            </MenuItem>
                        );
                    })}
                </Paper>
            </SubMenuPopover>

            <Box component="main" sx={{ flexGrow: 1, pt: 0, pb: 0, px: { xs: 1, sm: 1.5, md: 2 }, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
                <DrawerHeader />
                {renderContent}
            </Box>

            <Modal open={changePasswordModal} onClose={handleCloseAdvancedFilterModal}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <ChangePassword onClose={handleCloseAdvancedFilterModal} />
                </Box>
            </Modal>

            <Modal open={feedbackModal} onClose={handleCloseAdvancedFilterModal}>
                <Paper sx={{ padding: 4, maxWidth: 500, margin: '100px auto' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="h6" gutterBottom>Feedback</Typography>
                        <TextField
                            fullWidth
                            label="Subject"
                            value={feedback.subject}
                            onChange={(e) => handleInputChange('subject', e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Feedback"
                            value={feedback.feedback}
                            onChange={(e) => handleInputChange('feedback', e.target.value)}
                            margin="normal"
                            multiline
                            rows={4}
                        />
                        <Button variant="contained" color="primary" onClick={sentFeedback} sx={{ marginTop: 2 }}>
                            Send
                        </Button>
                    </Box>
                </Paper>
            </Modal>
        </Box>
    );
};

export default HomePage;